import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import liteIcon from "../assets/icons/LiteIcon.png";
import { AuthContext } from "../AuthContext";
import { API_URL } from "../config";
import {
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaLock,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { CountryModal } from "../components/modal/countryModal";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
} from "framer-motion";

const getOAuthConfig = async () => {
  const res = await axios.get(`${API_URL}/oauth-config/`);
  return res.data;
};

const FloatingIcon = ({ emoji, style, delay = 0, duration = 6 }) => (
  <motion.div
    style={{
      position: "absolute",
      fontSize: "clamp(22px, 2.8vw, 36px)",
      filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.22))",
      userSelect: "none",
      zIndex: 2,
      ...style,
    }}
    animate={{ y: [0, -22, 0], rotate: [-5, 5, -5], scale: [1, 1.1, 1] }}
    transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
  >
    {emoji}
  </motion.div>
);

const Ring = ({
  size,
  color,
  top,
  left,
  right,
  bottom,
  delay = 0,
  duration = 10,
  dashed,
}) => (
  <motion.div
    style={{
      position: "absolute",
      width: size,
      height: size,
      borderRadius: "50%",
      border: `${dashed ? "2px dashed" : "1.5px solid"} ${color}`,
      top,
      left,
      right,
      bottom,
      pointerEvents: "none",
    }}
    animate={{ rotate: 360 }}
    transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
  />
);

const Pulse = ({ style, delay = 0, color = "rgba(255,255,255,0.5)" }) => (
  <motion.div
    style={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: color,
      position: "absolute",
      ...style,
    }}
    animate={{ scale: [1, 2.4, 1], opacity: [0.7, 0, 0.7] }}
    transition={{ duration: 3.2, delay, repeat: Infinity, ease: "easeInOut" }}
  />
);

const PremiumInput = ({
  icon: Icon,
  label,
  name,
  type = "text",
  value,
  onChange,
  onFocus,
  onBlur,
  focused,
  suffix,
}) => (
  <div style={{ position: "relative" }}>
    <label
      style={{
        display: "block",
        fontSize: "0.67rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: focused ? "#0284c7" : "#64748b",
        marginBottom: 7,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        transition: "color 0.25s",
      }}
    >
      {label}
    </label>
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute",
          left: 15,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          color: focused ? "#0ea5e9" : "#94a3b8",
          display: "flex",
          alignItems: "center",
          transition: "color 0.25s",
        }}
      >
        <Icon size={14} />
      </div>
      <motion.input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        required
        placeholder={label}
        animate={{
          boxShadow: focused
            ? "0 0 0 3px rgba(14,165,233,0.18), 0 4px 20px rgba(14,165,233,0.12)"
            : "0 1px 4px rgba(0,0,0,0.05)",
        }}
        style={{
          width: "100%",
          padding: "13px 44px 13px 42px",
          borderRadius: 12,
          border: focused ? "2px solid #0ea5e9" : "2px solid #e2e8f0",
          background: focused ? "rgba(240,249,255,0.9)" : "#f8fafc",
          fontSize: "0.9rem",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: "#0f172a",
          outline: "none",
          transition: "border-color 0.25s, background 0.25s",
          boxSizing: "border-box",
        }}
      />
      {suffix && (
        <div
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          {suffix}
        </div>
      )}
    </div>
  </div>
);

const LoginPage = () => {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const { login } = useContext(AuthContext);
  const [countryModal, setCountryModal] = useState(false);
  const navigate = useNavigate();
  const [oauthLoading, setOauthLoading] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [focused, setFocused] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-300, 300], [3, -3]);
  const rotateY = useTransform(mouseX, [-300, 300], [-3, 3]);
  const cardRef = useRef(null);

  useEffect(() => {
    setMounted(true);
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    const fetchClientId = async () => {
      try {
        const c = await getOAuthConfig();
        setGoogleClientId(c.WEB_OAUTH_CLIENT_ID);
      } catch (e) {
        console.error(e);
      } finally {
        setOauthLoading(false);
      }
    };
    fetchClientId();
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  };
  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };
  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value.trim() }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/login-user/`, formData);
      const data = res.data;
      setSubmitDone(true);
      await login(data.token);
      toast.success("Login successful");
      setTimeout(() => {
        if (data.is_superuser) return navigate("/dashboard");
        if (data.is_staff) return navigate("/admin-exam-country");
        if (data.is_inst) return navigate("/insthome");
        navigate("/home");
      }, 600);
    } catch (err) {
      const msg =
        err.response?.data?.detail || "An error occurred. Please try again.";
      if (err.response?.status === 403 && msg === "Account is not active")
        return navigate("/verification");
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (cr) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/login-google/`, {
        id_token: cr.credential,
      });
      const data = res.data;
      await login({ access: data.access, refresh: data.refresh });
      toast.success("Google login successful");
      const token = JSON.parse(localStorage.getItem("authToken"));
      const pr = await fetch(`${API_URL}/student/profile/`, {
        headers: { Authorization: `Bearer ${token.access}` },
      });
      const pd = await pr.json();
      if (!pd.country) setCountryModal(true);
      else {
        if (data.is_superuser) return navigate("/dashboard");
        if (data.is_staff) return navigate("/admin-exam-country");
        if (data.is_inst) return navigate("/insthome");
        navigate("/home");
      }
    } catch {
      toast.error("Google login failed");
    }
    setLoading(false);
  };

  const E = [0.16, 1, 0.3, 1];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: #f8fafc; overflow-x: hidden; }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px #f8fafc inset !important; -webkit-text-fill-color: #0f172a !important; }
        ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-thumb { background: #bae6fd; border-radius: 8px; }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          background: "#f8fafc",
        }}
      >
        {/* ══ LEFT PANEL – desktop only ══ */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, x: -90 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, ease: E }}
            style={{
              flex: "0 0 44%",
              minHeight: "100vh",
              background:
                "linear-gradient(150deg, #0369a1 0%, #0284c7 28%, #0ea5e9 62%, #38bdf8 100%)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 40px",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)",
                backgroundSize: "30px 30px",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "25%",
                left: "20%",
                width: 360,
                height: 360,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 65%)",
                pointerEvents: "none",
              }}
            />

            {/* Rings */}
            <Ring
              size={400}
              color="rgba(255,255,255,0.07)"
              top="-90px"
              left="-90px"
              duration={24}
            />
            <Ring
              size={280}
              color="rgba(255,255,255,0.11)"
              top="45%"
              left="48%"
              duration={18}
              delay={2}
            />
            <Ring
              size={190}
              color="rgba(251,191,36,0.28)"
              bottom="-50px"
              right="-50px"
              duration={14}
              delay={3}
              dashed
            />
            <Ring
              size={130}
              color="rgba(255,255,255,0.16)"
              top="6%"
              left="58%"
              duration={9}
              delay={1}
            />
            <Ring
              size={90}
              color="rgba(251,191,36,0.35)"
              top="72%"
              left="4%"
              duration={7}
              delay={5}
              dashed
            />

            {/* Pulses */}
            {[
              { s: { top: "16%", left: "10%" }, d: 0 },
              { s: { top: "62%", left: "84%" }, d: 1 },
              {
                s: { top: "84%", left: "26%" },
                d: 2,
                c: "rgba(251,191,36,0.8)",
              },
              { s: { top: "10%", left: "80%" }, d: 3 },
              {
                s: { top: "42%", left: "3%" },
                d: 1.5,
                c: "rgba(251,191,36,0.7)",
              },
            ].map((p, i) => (
              <Pulse key={i} style={p.s} delay={p.d} color={p.c} />
            ))}

            {/* Floating icons */}
            <FloatingIcon
              emoji="📚"
              style={{ top: "7%", left: "7%" }}
              delay={0}
              duration={5.5}
            />
            <FloatingIcon
              emoji="🎓"
              style={{ top: "10%", right: "7%" }}
              delay={0.9}
              duration={6.5}
            />
            <FloatingIcon
              emoji="✏️"
              style={{ top: "50%", left: "3%" }}
              delay={1.7}
              duration={5}
            />
            <FloatingIcon
              emoji="🔬"
              style={{ bottom: "20%", right: "5%" }}
              delay={0.5}
              duration={7}
            />
            <FloatingIcon
              emoji="🧮"
              style={{ bottom: "7%", left: "13%" }}
              delay={2.1}
              duration={6.2}
            />
            <FloatingIcon
              emoji="🏆"
              style={{ top: "36%", right: "3%" }}
              delay={1.3}
              duration={5.8}
            />
            <FloatingIcon
              emoji="💡"
              style={{ top: "66%", left: "46%" }}
              delay={2.9}
              duration={6.5}
            />
            <FloatingIcon
              emoji="🎯"
              style={{ top: "27%", left: "40%" }}
              delay={3.4}
              duration={7.2}
            />

            {/* Gold bar */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.8, duration: 1.4, ease: E }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 5,
                background:
                  "linear-gradient(90deg,#f59e0b,#fbbf24,#fde68a,#fbbf24,#f59e0b)",
                transformOrigin: "left",
              }}
            />

            {/* Content */}
            <div
              style={{
                position: "relative",
                zIndex: 3,
                textAlign: "center",
                maxWidth: 360,
              }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  delay: 0.4,
                  duration: 1,
                  type: "spring",
                  stiffness: 140,
                }}
              >
                <Link to="/" style={{ display: "inline-block" }}>
                  <motion.div
                    whileHover={{ scale: 1.08, rotate: 3 }}
                    transition={{ type: "spring", stiffness: 250 }}
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: 30,
                      background: "rgba(255,255,255,0.18)",
                      backdropFilter: "blur(20px)",
                      border: "2px solid rgba(255,255,255,0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 32px",
                      boxShadow:
                        "0 20px 60px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.4)",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={liteIcon}
                      alt="LiteUpper"
                      style={{
                        width: 64,
                        height: 64,
                        objectFit: "contain",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                  </motion.div>
                </Link>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.9, ease: E }}
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(30px, 3.5vw, 48px)",
                  fontWeight: 900,
                  color: "#fff",
                  lineHeight: 1.12,
                  marginBottom: 18,
                  letterSpacing: "-0.5px",
                  textShadow: "0 2px 24px rgba(0,0,0,0.12)",
                }}
              >
                Unlock Your
                <span
                  style={{
                    display: "block",
                    background:
                      "linear-gradient(135deg, #fde68a, #fbbf24, #f59e0b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Full Potential
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8, ease: E }}
                style={{
                  color: "rgba(255,255,255,0.84)",
                  fontSize: "1rem",
                  lineHeight: 1.75,
                  marginBottom: 36,
                  fontWeight: 500,
                }}
              >
                Join thousands of students achieving excellence through guided
                learning and smart preparation.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.88, duration: 0.8, ease: E }}
                style={{
                  display: "flex",
                  gap: 14,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { value: "50K+", label: "Students" },
                  { value: "200+", label: "Courses" },
                  { value: "98%", label: "Pass Rate" },
                ].map((s) => (
                  <motion.div
                    key={s.label}
                    whileHover={{ scale: 1.08, y: -5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      borderRadius: 18,
                      padding: "16px 22px",
                      textAlign: "center",
                      minWidth: 88,
                      boxShadow:
                        "0 8px 28px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.2)",
                      cursor: "default",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: "1.7rem",
                        fontWeight: 900,
                        color: "#fbbf24",
                        lineHeight: 1,
                        marginBottom: 5,
                      }}
                    >
                      {s.value}
                    </div>
                    <div
                      style={{
                        fontSize: "0.68rem",
                        color: "rgba(255,255,255,0.78)",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      {s.label}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              style={{
                position: "absolute",
                bottom: 32,
                left: 40,
                right: 40,
                borderLeft: "3px solid #fbbf24",
                paddingLeft: 16,
              }}
            >
              <p
                style={{
                  color: "rgba(255,255,255,0.68)",
                  fontSize: "0.8rem",
                  fontStyle: "italic",
                  fontFamily: "'Playfair Display', serif",
                  lineHeight: 1.65,
                }}
              >
                "Education is the most powerful weapon which you can use to
                change the world."
              </p>
              <p
                style={{
                  color: "rgba(255,255,255,0.42)",
                  fontSize: "0.7rem",
                  marginTop: 6,
                  fontWeight: 700,
                }}
              >
                — Nelson Mandela
              </p>
            </motion.div>
          </motion.div>
        )}

        {/* ══ RIGHT PANEL ══ */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "70px 16px 32px" : "40px 24px",
            position: "relative",
            background: "#f8fafc",
            minHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundImage:
                "radial-gradient(circle at 85% 15%, rgba(14,165,233,0.07) 0%, transparent 45%), radial-gradient(circle at 10% 85%, rgba(251,191,36,0.05) 0%, transparent 40%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />

          {/* Back button */}
          <AnimatePresence>
            {mounted && (
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                onClick={() => navigate(-1)}
                whileHover={{ x: -3 }}
                whileTap={{ scale: 0.92 }}
                style={{
                  position: "fixed",
                  top: 20,
                  left: 20,
                  zIndex: 100,
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  background: "rgba(255,255,255,0.95)",
                  border: "1.5px solid rgba(14,165,233,0.25)",
                  borderRadius: 12,
                  padding: "9px 16px",
                  color: "#0369a1",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  backdropFilter: "blur(12px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  transition: "all 0.2s",
                }}
              >
                <FaArrowLeft size={11} /> Back
              </motion.button>
            )}
          </AnimatePresence>

          {/* Card */}
          <motion.div
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              rotateX: isMobile ? 0 : rotateX,
              rotateY: isMobile ? 0 : rotateY,
              width: "100%",
              maxWidth: 460,
              position: "relative",
              zIndex: 1,
            }}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: E, delay: 0.15 }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: isMobile ? 20 : 26,
                boxShadow:
                  "0 32px 100px rgba(3,105,161,0.12), 0 4px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
                border: "1px solid rgba(14,165,233,0.1)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: 4,
                  background:
                    "linear-gradient(90deg, #0369a1, #0284c7, #38bdf8, #fbbf24)",
                }}
              />

              <div
                style={{
                  padding: isMobile ? "28px 22px 26px" : "40px 40px 36px",
                }}
              >
                {/* Logo in right card — always visible on mobile, also on desktop */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.28, duration: 0.6, type: "spring" }}
                  style={{ textAlign: "center", marginBottom: 20 }}
                >
                  <Link to="/">
                    <motion.img
                      src={liteIcon}
                      alt="LiteUpper"
                      whileHover={{ scale: 1.1, rotate: 4 }}
                      transition={{ type: "spring", stiffness: 260 }}
                      style={{
                        height: isMobile ? 68 : 80,
                        width: isMobile ? 68 : 80,
                        objectFit: "contain",
                        filter: "drop-shadow(0 8px 20px rgba(14,165,233,0.32))",
                        cursor: "pointer",
                      }}
                    />
                  </Link>
                </motion.div>

                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.38, duration: 0.7 }}
                  style={{ textAlign: "center", marginBottom: 26 }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
                      border: "1px solid rgba(14,165,233,0.22)",
                      borderRadius: 100,
                      padding: "7px 16px",
                      marginBottom: 14,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>👋</span>
                    <span
                      style={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        color: "#0369a1",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Welcome Back
                    </span>
                  </div>
                  <h2
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: isMobile ? "1.55rem" : "1.85rem",
                      fontWeight: 900,
                      color: "#0c1a2e",
                      letterSpacing: "-0.4px",
                      lineHeight: 1.2,
                      marginBottom: 8,
                    }}
                  >
                    Sign in to LiteUpper
                  </h2>
                  <p
                    style={{
                      color: "#64748b",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    Continue your learning journey today
                  </p>
                </motion.div>

                <form onSubmit={handleSubmit}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 18,
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.48, duration: 0.6 }}
                    >
                      <PremiumInput
                        icon={FaEnvelope}
                        label="Username or Email"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        focused={focused === "username"}
                        onFocus={() => setFocused("username")}
                        onBlur={() => setFocused(null)}
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.58, duration: 0.6 }}
                    >
                      <PremiumInput
                        icon={FaLock}
                        label="Password"
                        name="password"
                        type={showPass ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        focused={focused === "password"}
                        onFocus={() => setFocused("password")}
                        onBlur={() => setFocused(null)}
                        suffix={
                          <motion.button
                            type="button"
                            onClick={() => setShowPass((p) => !p)}
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.88 }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: showPass ? "#0ea5e9" : "#94a3b8",
                              display: "flex",
                              padding: 4,
                              transition: "color 0.2s",
                            }}
                          >
                            {showPass ? (
                              <FaEyeSlash size={14} />
                            ) : (
                              <FaEye size={14} />
                            )}
                          </motion.button>
                        }
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.66 }}
                      style={{ textAlign: "right", marginTop: -6 }}
                    >
                      <motion.button
                        type="button"
                        onClick={() => navigate("/send-reset-password")}
                        whileHover={{ x: 3 }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#0369a1",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          padding: 0,
                        }}
                      >
                        Forgot password? →
                      </motion.button>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.74, duration: 0.6 }}
                    >
                      <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={
                          !loading
                            ? {
                                scale: 1.02,
                                boxShadow: "0 20px 50px rgba(2,132,199,0.5)",
                              }
                            : {}
                        }
                        whileTap={!loading ? { scale: 0.97 } : {}}
                        animate={
                          submitDone
                            ? {
                                background: [
                                  "linear-gradient(135deg,#0284c7,#38bdf8)",
                                  "linear-gradient(135deg,#059669,#34d399)",
                                ],
                              }
                            : {}
                        }
                        style={{
                          width: "100%",
                          padding: "15px 24px",
                          borderRadius: 14,
                          border: "none",
                          background: loading
                            ? "rgba(14,165,233,0.55)"
                            : "linear-gradient(135deg, #0369a1 0%, #0284c7 45%, #0ea5e9 100%)",
                          color: "#fff",
                          fontSize: "0.975rem",
                          fontWeight: 800,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          cursor: loading ? "not-allowed" : "pointer",
                          boxShadow: "0 10px 32px rgba(2,132,199,0.38)",
                          transition: "box-shadow 0.25s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 10,
                          letterSpacing: "0.02em",
                        }}
                      >
                        <AnimatePresence mode="wait">
                          {loading ? (
                            <motion.span
                              key="l"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <CircularProgress
                                size={17}
                                sx={{ color: "#fff" }}
                              />{" "}
                              Signing in…
                            </motion.span>
                          ) : (
                            <motion.span
                              key="t"
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                            >
                              Sign In →
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.button>
                    </motion.div>
                  </div>
                </form>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.84 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    margin: "22px 0",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      background:
                        "linear-gradient(90deg, transparent, #e2e8f0)",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.67rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    or continue with
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      background:
                        "linear-gradient(90deg, #e2e8f0, transparent)",
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.92 }}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: 22,
                  }}
                >
                  {oauthLoading ? (
                    <CircularProgress size={20} sx={{ color: "#0284c7" }} />
                  ) : googleClientId ? (
                    <GoogleOAuthProvider clientId={googleClientId}>
                      <GoogleLogin
                        onSuccess={handleGoogleLogin}
                        onError={() => toast.error("Google Login Failed")}
                        shape="pill"
                        theme="outline"
                        size="large"
                        text="continue_with"
                      />
                    </GoogleOAuthProvider>
                  ) : (
                    <span style={{ color: "#ef4444", fontSize: "0.8rem" }}>
                      Google login unavailable
                    </span>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  style={{ textAlign: "center" }}
                >
                  <span style={{ color: "#64748b", fontSize: "0.875rem" }}>
                    Don't have institution account?{" "}
                  </span>
                  <motion.button
                    type="button"
                    onClick={() => navigate("/register")}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.96 }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 800,
                      color: "#0284c7",
                      fontSize: "0.875rem",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      padding: 0,
                      textDecoration: "underline",
                      textDecorationColor: "rgba(2,132,199,0.3)",
                      textUnderlineOffset: 3,
                    }}
                  >
                    Create institution account
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <CountryModal
        open={countryModal}
        onSuccess={() => {
          setCountryModal(false);
          navigate("/home");
        }}
      />
    </>
  );
};

export default LoginPage;
